import { Injectable } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { CollectionPaths } from '@models/collections';
import {
	GlobalEmailTemplate,
	GlobalEmailTemplateMetadata,
} from '@models/global-email-templates';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Action, State, StateContext } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { GlobalEmailTemplateVersionsRepo } from '@repos/email-templates/global-email-templates/global-email-template-versions.repo';
import { GlobalEmailTemplatesRepo } from '@repos/email-templates/global-email-templates/global-email-templates.repo';
import { AuthState } from '@state/auth';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { FirestoreStateBaseForGlobalWithSubscription } from '@state/FirestoreStateBaseForGlobal';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import * as actions from './global-email-templates.actions';
import { GlobalEmailTemplatesStateModel } from './global-email-templates.models';

const defaultTemplateVersion: Omit<GlobalEmailTemplate, 'id'> = {
	subject: '',
	htmlMessage: '',
	attachments: [],
	createdBy: '',
	createdAt: 0,
};

const DEFAULT_STATE: GlobalEmailTemplatesStateModel = {
	...DEFAULT_STATE_BASE,
	templates: [],
	templateVersions: {},
	error: null,
};

@State<GlobalEmailTemplatesStateModel>({
	name: 'globalEmailTemplates',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class GlobalEmailTemplatesState extends FirestoreStateBaseForGlobalWithSubscription<
	GlobalEmailTemplateMetadata,
	GlobalEmailTemplatesStateModel
> {
	@SelectSnapshot(AuthState.getUserId) private userId: string;

	constructor(
		private globalEmailTemplatesRepo: GlobalEmailTemplatesRepo,
		private globalEmailTemplateVersionsRepo: GlobalEmailTemplateVersionsRepo,
	) {
		super(
			() => CollectionPaths.global_email_templates(),
			(payload) => ({ templates: payload as GlobalEmailTemplateMetadata[] }),
			() => DEFAULT_STATE,
		);
	}

	@Action(actions.CreateGlobalEmailTemplate)
	async createTemplate(
		ctx: StateContext<GlobalEmailTemplatesStateModel>,
		{ template, afterCreateAction }: actions.CreateGlobalEmailTemplate,
	) {
		let createdTemplate: DocumentReference<GlobalEmailTemplateMetadata>;

		ctx.setState(
			patch<GlobalEmailTemplatesStateModel>({
				error: null,
			}),
		);

		try {
			createdTemplate = await this.repo.Add(template);
			const templateVersion = await this.globalEmailTemplateVersionsRepo.Create(
				{
					subject: '',
					htmlMessage: '',
					attachments: [],
					createdBy: this.userId,
					createdAt: Date.now(),
				},
				{ templateId: createdTemplate.id },
			);
			if (templateVersion) {
				await this.repo.CreateOrMerge(createdTemplate.id, {
					...template,
					versionId: templateVersion.id,
				});
			}
			if (afterCreateAction) {
				afterCreateAction(createdTemplate.id);
			}
		} catch (error) {
			ctx.setState(
				patch<GlobalEmailTemplatesStateModel>({
					error:
						error instanceof Error ? error.message : 'Unknown error occurred',
				}),
			);
			throw error;
		}
	}

	@Action(actions.UpdateGlobalEmailTemplate)
	async updateTemplate(
		ctx: StateContext<GlobalEmailTemplatesStateModel>,
		{
			templateId,
			templateMetadata,
			templateVersion,
		}: actions.UpdateGlobalEmailTemplate,
	) {
		try {
			const templateVersionData = {
				...defaultTemplateVersion,
				...templateVersion,
				createdBy: this.userId,
				createdAt: Date.now(),
			};
			const templateVersionDataRef =
				await this.globalEmailTemplateVersionsRepo.Create(templateVersionData, {
					templateId,
				});

			if (templateVersionDataRef) {
				await this.repo.CreateOrMerge(templateId, {
					...templateMetadata,
					versionId: templateVersionDataRef.id,
				});

				ctx.setState(
					patch<GlobalEmailTemplatesStateModel>({
						templates: updateItem<GlobalEmailTemplateMetadata>(
							(template) => template.id === templateId,
							patch({
								...templateMetadata,
								versionId: templateVersionDataRef.id,
							}),
						),
						templateVersions: patch({
							[templateId]: patch({
								[templateVersionDataRef.id]: templateVersionData,
							}),
						}),
					}),
				);
			} else {
				throw new Error('Failed to create template version');
			}
		} catch (error) {
			ctx.setState(
				patch<GlobalEmailTemplatesStateModel>({
					error:
						error instanceof Error ? error.message : 'Unknown error occurred',
				}),
			);
			throw error;
		}
	}

	@Action(actions.DeleteGlobalEmailTemplate)
	deleteTemplate(
		ctx: StateContext<GlobalEmailTemplatesStateModel>,
		{ id }: actions.DeleteGlobalEmailTemplate,
	) {
		this.repo.Delete(id);
	}

	@Action(actions.LoadGlobalEmailTemplateVersions)
	async loadTemplateVersion(
		ctx: StateContext<GlobalEmailTemplatesStateModel>,
		{ templateId }: actions.LoadGlobalEmailTemplateVersions,
	) {
		const state = ctx.getState();
		const template = state.templates.find((t) => t.id === templateId);

		if (template?.versionId) {
			try {
				const versions = await lastValueFrom(
					this.globalEmailTemplateVersionsRepo
						.All({ templateId })
						.pipe(take(1)),
				);
				if (versions) {
					ctx.setState(
						patch<GlobalEmailTemplatesStateModel>({
							templateVersions: patch({
								[templateId]: Object.fromEntries(
									versions.map((version) => [version.id, version]),
								),
							}),
						}),
					);
				}
			} catch (error) {
				ctx.setState(
					patch<GlobalEmailTemplatesStateModel>({
						error:
							error instanceof Error
								? error.message
								: 'Failed to load template version',
					}),
				);
			}
		}
	}
}
